import { BankConnectionCreate } from '../../clients/rust-api-client';
import { getBankErrorParts } from '../../utils/bankHelpers';

/**
 * The new-bank-connection modal's draft and the pure rules over it (#665,
 * the banking half). A connection is a company's credential for a bank,
 * and creating one **verifies it against the live bank** first. Repeated
 * invalid credentials lock the account, and only the bank can unlock it
 * (knowledge/notes/gotcha-invalid-creds-lock-account.md). So a pair the
 * bank has already refused is remembered here, and cannot be sent again
 * unchanged.
 */

// Supported banks. The backend `banks` catalog has no list endpoint yet,
// so the options are declared here. `providerKey` is sent as the
// connection's provider_key; the backend fills in base_url and
// api_version per bank.
export const BANK_OPTIONS: { providerKey: string; label: string }[] = [
  { providerKey: 'columbia', label: 'Columbia' },
];
export const DEFAULT_PROVIDER_KEY = BANK_OPTIONS[0].providerKey;

export interface BankConnectionDraft {
  companyId: string | null;
  providerKey: string;
  label: string;
  clientId: string;
  clientSecret: string;
  lockboxCompanyId: string;
}

export const emptyBankConnectionDraft = (
  companyId: string | null
): BankConnectionDraft => ({
  companyId,
  providerKey: DEFAULT_PROVIDER_KEY,
  label: '',
  clientId: '',
  clientSecret: '',
  lockboxCompanyId: '',
});

/** The credential the bank judged: what makes a retry a *different* try. */
export const credentialKey = (d: BankConnectionDraft): string =>
  JSON.stringify([d.providerKey, d.clientId.trim(), d.clientSecret]);

/** Required and empty, as "Still needs …" phrases. */
export const missingForBankConnection = (d: BankConnectionDraft): string[] =>
  [
    !d.companyId && 'a company on your account',
    !d.clientId.trim() && 'a client ID',
    !d.clientSecret.trim() && 'a client secret',
  ].filter(Boolean) as string[];

/**
 * The request for a complete draft, or null. Also null when this exact
 * credential was already refused by the bank (`rejectedKey`): sending it
 * again cannot succeed and counts toward the lockout.
 */
export const buildBankConnectionCreate = (
  d: BankConnectionDraft,
  rejectedKey: string | null = null
): BankConnectionCreate | null => {
  if (missingForBankConnection(d).length || !d.companyId) return null;
  if (rejectedKey !== null && rejectedKey === credentialKey(d)) return null;
  return {
    company_id: d.companyId,
    provider_key: d.providerKey,
    client_id: d.clientId.trim(),
    // Sent exactly as typed: a secret's spaces are part of it.
    client_secret: d.clientSecret,
    label: d.label.trim() || null,
    lockbox_company_id: d.lockboxCompanyId.trim() || null,
  };
};

/**
 * Bank handler errors are plain text, not JSON. The title is keyed off the
 * status; the server's own words are shown where it gave any.
 */
export const bankConnectionError = (
  err: unknown
): { title: string; message: string } => {
  // `body` is '' when the server gave no reason; each case supplies its own.
  const { status, body } = getBankErrorParts(err);

  switch (status) {
    case 400:
      return {
        title: 'Credential verification failed',
        message:
          body ||
          'The bank rejected these credentials. Double-check the client ID and secret before trying again.',
      };
    case 502:
    case 503:
      return {
        title: "Couldn't reach the bank",
        message:
          body ||
          'The credentials could not be verified because the bank was unreachable. This environment may not be whitelisted for bank access.',
      };
    case 409:
      return {
        title: 'Cannot delete connection',
        message:
          body ||
          'This connection still has accounts assigned. Unassign them before deleting it.',
      };
    case 403:
      return {
        title: 'Not authorized',
        message: body || 'You do not have permission to perform this action.',
      };
    default:
      return {
        title: 'Something went wrong',
        message: body || 'Please try again.',
      };
  }
};

/**
 * Did the bank judge the credential itself, rather than fail to answer?
 * The API says so with `credentials_invalid`. Any other 400 except
 * `invalid_request` (our own input check) is treated the same way. That
 * leans toward blocking a resend: a wrongly blocked retry costs a retype,
 * a wrongly allowed one counts toward a lockout.
 */
export const isCredentialRejection = (err: unknown): boolean => {
  const { status, code } = getBankErrorParts(err);
  return status === 400 && code !== 'invalid_request';
};
